// State machine for the property create / edit wizard.
//
// Two concerns:
//
//  1. {@link PropertyDraft} — the typed working copy that the form
//     mutates. Mirrors PropertyCreateRequest but every field is
//     optional so we can persist an incomplete draft locally and let
//     the user fill the rest later. Conversion to the wire shape
//     happens at submit time via {@link draftToCreateRequest} /
//     {@link draftToPatchRequest}.
//
//  2. {@link WIZARD_STEPS} + helpers — the 4-step navigation contract
//     and the per-step validation rules.
//
// Everything here is pure (no React, no router). The wizard layout
// component composes these into the live flow.

import type { components } from "@/types/api";
import type {
  BrokerageFeeType,
  PropertyDirection,
  PropertyFurniture,
  PropertyType,
} from "@/features/property/utils/property-enum-options";

// ---------------------------------------------------------------------------
// Draft type — superset of create / edit, every field optional
// ---------------------------------------------------------------------------

/**
 * Every field is `T | undefined` (not optional via `?`) so the
 * exactOptionalPropertyTypes setting accepts both "missing key" and
 * "key present, undefined value". The onChange handlers in the
 * steps need to clear fields by setting them to undefined.
 */
export interface PropertyDraft {
  // Basic info
  buildingName?: string | undefined;
  /** Set when the unit is linked to a project; drives the shared-field prefill
   *  + lock and the auto-numbered internal code. Undefined = free / unlinked. */
  projectId?: number | undefined;
  streetAddress?: string | undefined;
  wardCode?: string | undefined;
  propertyType?: PropertyType | undefined;
  listingType?: "SALE" | "RENT" | undefined;
  internalCode?: string | undefined;
  assignedBrokerId?: number | undefined;

  // Details
  area?: number | undefined;
  floor?: number | undefined;
  /** "Trục" — the unit's axis/stack in the building ("05A", "12"). */
  axis?: string | undefined;
  totalFloors?: number | undefined;
  bedrooms?: number | undefined;
  bathrooms?: number | undefined;
  direction?: PropertyDirection | undefined;
  furniture?: PropertyFurniture | undefined;
  price?: number | undefined;
  description?: string | undefined;
  // Brokerage commission — a typed value (months / percent / VND amount).
  brokerageFeeType?: BrokerageFeeType | undefined;
  brokerageFeeValue?: number | undefined;
  // Explicit "clear the fee" intent (set when the user picks "Không có" on an
  // edit). Distinguishes a removed fee from an untouched one on PATCH.
  clearBrokerageFee?: boolean | undefined;

  // Publish
  status?: "AVAILABLE" | "DEPOSIT" | "SOLD" | "RENTED" | undefined;
}

export const EMPTY_DRAFT: PropertyDraft = {};

// ---------------------------------------------------------------------------
// Steps
// ---------------------------------------------------------------------------

export type WizardStep = "basic" | "details" | "images" | "publish";

interface WizardStepDescriptor {
  key: WizardStep;
  /** Vietnamese label shown in the step rail. */
  label: string;
}

export const WIZARD_STEPS: readonly WizardStepDescriptor[] = [
  { key: "basic", label: "Thông tin cơ bản" },
  { key: "details", label: "Đặc điểm" },
  { key: "images", label: "Hình ảnh & Media" },
  { key: "publish", label: "Xuất bản" },
];

/** Zero-based index of {@link step} within {@link WIZARD_STEPS}. */
export function stepIndex(step: WizardStep): number {
  return WIZARD_STEPS.findIndex((s) => s.key === step);
}

/** Returns the next step in the flow, or {@code null} at the end. */
export function nextStep(step: WizardStep): WizardStep | null {
  const idx = stepIndex(step);
  const next = WIZARD_STEPS[idx + 1];
  return next ? next.key : null;
}

/** Returns the previous step in the flow, or {@code null} at the start. */
export function prevStep(step: WizardStep): WizardStep | null {
  const idx = stepIndex(step);
  const prev = WIZARD_STEPS[idx - 1];
  return prev ? prev.key : null;
}

// ---------------------------------------------------------------------------
// Per-step validation
// ---------------------------------------------------------------------------

export interface StepValidation {
  ok: boolean;
  /** Per-field error messages. Empty when {@code ok} is true. */
  errors: Partial<Record<keyof PropertyDraft, string>>;
}

/**
 * Validate that the user has filled enough fields to advance from the
 * given step. Required fields per step mirror the design comp's visual
 * "required" indicators + what the backend actually demands at submit.
 */
export function validateStep(step: WizardStep, draft: PropertyDraft): StepValidation {
  const errors: Partial<Record<keyof PropertyDraft, string>> = {};
  switch (step) {
    case "basic": {
      if (!draft.buildingName?.trim()) {
        errors.buildingName = "Vui lòng nhập tên toà nhà";
      }
      if (!draft.wardCode) {
        errors.wardCode = "Vui lòng chọn khu vực";
      }
      if (!draft.propertyType) {
        errors.propertyType = "Vui lòng chọn loại bất động sản";
      }
      if (!draft.listingType) {
        errors.listingType = "Vui lòng chọn hình thức giao dịch";
      }
      break;
    }
    case "details": {
      if (draft.area == null || !Number.isFinite(draft.area) || draft.area <= 0) {
        errors.area = "Diện tích phải lớn hơn 0";
      }
      if (draft.price == null || !Number.isFinite(draft.price) || draft.price <= 0) {
        errors.price = "Giá phải lớn hơn 0";
      }
      break;
    }
    case "images":
      // Images step has no required content for submit — properties can
      // ship without photos. UI nudges the user, doesn't gate.
      break;
    case "publish":
      // Same as images: status defaults to AVAILABLE.
      break;
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** True when every step's validation passes. */
export function isDraftSubmittable(draft: PropertyDraft): boolean {
  return WIZARD_STEPS.every((s) => validateStep(s.key, draft).ok);
}

// ---------------------------------------------------------------------------
// Wire-shape conversion
// ---------------------------------------------------------------------------

type CreateRequest = components["schemas"]["PropertyCreateRequest"];
type UpdateRequest = components["schemas"]["PropertyUpdateInfoRequest"];

/**
 * Convert a draft to the {@code POST /properties} wire shape.
 *
 * Throws if a required field is missing. Callers should call
 * {@link isDraftSubmittable} first.
 */
export function draftToCreateRequest(draft: PropertyDraft): CreateRequest {
  // Validation is encoded in {@link isDraftSubmittable}; this function
  // asserts to make TS happy after the runtime check.
  if (
    draft.listingType == null ||
    draft.wardCode == null ||
    draft.area == null ||
    draft.price == null
  ) {
    throw new Error("draftToCreateRequest: draft is missing required fields");
  }
  const body: CreateRequest = {
    listingType: draft.listingType,
    wardCode: draft.wardCode,
    area: draft.area,
    price: draft.price,
  };
  // When linked to a project, the backend copies the shared fields from the
  // project and auto-numbers the code — we still send buildingName etc. (the
  // prefilled values) but projectId is what makes the link authoritative.
  if (draft.projectId != null) body.projectId = draft.projectId;
  if (draft.buildingName != null) body.buildingName = draft.buildingName;
  if (draft.streetAddress != null) body.streetAddress = draft.streetAddress;
  if (draft.propertyType != null) body.propertyType = draft.propertyType;
  if (draft.internalCode != null) body.internalCode = draft.internalCode;
  if (draft.assignedBrokerId != null) body.assignedBrokerId = draft.assignedBrokerId;
  if (draft.floor != null) body.floor = draft.floor;
  // Truthy check: a typed-then-erased axis ("") means "not set" on create
  // (there is nothing to clear yet — "" only matters as "clear" on PATCH).
  if (draft.axis) body.axis = draft.axis;
  if (draft.totalFloors != null) body.totalFloors = draft.totalFloors;
  if (draft.bedrooms != null) body.bedrooms = draft.bedrooms;
  if (draft.bathrooms != null) body.bathrooms = draft.bathrooms;
  if (draft.direction != null) body.direction = draft.direction;
  if (draft.furniture != null) body.furniture = draft.furniture;
  if (draft.description != null) body.description = draft.description;
  if (draft.brokerageFeeType != null) {
    body.brokerageFeeType = draft.brokerageFeeType;
    if (draft.brokerageFeeValue != null) {
      body.brokerageFeeValue = draft.brokerageFeeValue;
    }
  }
  return body;
}

/**
 * Convert a draft to the {@code PATCH /properties/{id}} wire shape.
 *
 * Every field is optional — the backend treats missing fields as
 * "no change". We only emit fields that are set (not undefined).
 */
export function draftToPatchRequest(draft: PropertyDraft): UpdateRequest {
  const body: UpdateRequest = {};
  if (draft.listingType !== undefined) body.listingType = draft.listingType;
  if (draft.buildingName !== undefined) body.buildingName = draft.buildingName;
  if (draft.streetAddress !== undefined) body.streetAddress = draft.streetAddress;
  if (draft.wardCode !== undefined) body.wardCode = draft.wardCode;
  if (draft.propertyType !== undefined) body.propertyType = draft.propertyType;
  if (draft.internalCode !== undefined) body.internalCode = draft.internalCode;
  if (draft.assignedBrokerId !== undefined) body.assignedBrokerId = draft.assignedBrokerId;
  if (draft.floor !== undefined) body.floor = draft.floor;
  // Free-text semantics on PATCH: "" = clear the axis, absent = no change.
  if (draft.axis !== undefined) body.axis = draft.axis;
  if (draft.totalFloors !== undefined) body.totalFloors = draft.totalFloors;
  if (draft.bedrooms !== undefined) body.bedrooms = draft.bedrooms;
  if (draft.bathrooms !== undefined) body.bathrooms = draft.bathrooms;
  if (draft.area !== undefined) body.area = draft.area;
  if (draft.direction !== undefined) body.direction = draft.direction;
  if (draft.furniture !== undefined) body.furniture = draft.furniture;
  if (draft.description !== undefined) body.description = draft.description;
  // A set type writes the fee; an explicit clear flag (user picked "Không có")
  // removes it. An untouched fee emits nothing → no change.
  if (draft.brokerageFeeType != null) {
    body.brokerageFeeType = draft.brokerageFeeType;
    if (draft.brokerageFeeValue != null) {
      body.brokerageFeeValue = draft.brokerageFeeValue;
    }
  } else if (draft.clearBrokerageFee === true) {
    body.clearBrokerageFee = true;
  }
  return body;
}

// ---------------------------------------------------------------------------
// Loading: Property → Draft
// ---------------------------------------------------------------------------

/** Build a draft from an existing property (for the edit flow). */
export function draftFromProperty(
  property: components["schemas"]["PropertyResponse"],
): PropertyDraft {
  const draft: PropertyDraft = {};
  if (property.buildingName != null) draft.buildingName = property.buildingName;
  if (property.projectId != null) draft.projectId = property.projectId;
  if (property.streetAddress != null) draft.streetAddress = property.streetAddress;
  if (property.wardCode != null) draft.wardCode = property.wardCode;
  if (property.propertyType != null) draft.propertyType = property.propertyType;
  if (property.listingType != null) draft.listingType = property.listingType;
  if (property.internalCode != null) draft.internalCode = property.internalCode;
  if (property.assignedBrokerId != null) draft.assignedBrokerId = property.assignedBrokerId;
  if (property.area != null) draft.area = property.area;
  if (property.floor != null) draft.floor = property.floor;
  if (property.axis != null) draft.axis = property.axis;
  if (property.totalFloors != null) draft.totalFloors = property.totalFloors;
  if (property.bedrooms != null) draft.bedrooms = property.bedrooms;
  if (property.bathrooms != null) draft.bathrooms = property.bathrooms;
  if (property.direction != null) draft.direction = property.direction;
  if (property.furniture != null) draft.furniture = property.furniture;
  if (property.price != null) draft.price = property.price;
  if (property.description != null) draft.description = property.description;
  if (property.brokerageFeeType != null) draft.brokerageFeeType = property.brokerageFeeType;
  if (property.brokerageFeeValue != null) draft.brokerageFeeValue = property.brokerageFeeValue;
  if (property.status != null) draft.status = property.status;
  return draft;
}
